import { useMemo } from 'react';
import type { Employee, EmployeeMonthlyInfo } from '../types/employee';
import type { WorkSchedule } from '../types/employee';
import { useRatesStore } from '../store/ratesStore';
import { ratesDefault2026 } from '../constants/regulatoryRatesDefault';
import {
  buildMonthlyInfoList,
  buildMonthlyPayrollData,
  type BuildMonthlyPayrollOptions,
} from '../lib/payroll/monthly-info-builder';
import { buildDailyWorkerNotices } from '../lib/payroll/daily-worker-advisor';
import { employeesForMonthlyPayroll } from '../utils/workScheduleDisplay';
import { getStartOfMonth, getEndOfMonth } from '../utils/dateUtils';

export function useEmployeePayroll(
  employees: Employee[],
  schedules: WorkSchedule[],
  selectedMonth: Date,
  options: BuildMonthlyPayrollOptions = {},
) {
  const rates = useRatesStore((s) => s.current);

  const monthStart = getStartOfMonth(selectedMonth);
  const monthEnd = getEndOfMonth(selectedMonth);

  const schedulesInMonth = useMemo(
    () =>
      schedules.filter((schedule) => {
        const scheduleDate = new Date(schedule.date);
        if (isNaN(scheduleDate.getTime())) return false;
        return scheduleDate >= monthStart && scheduleDate <= monthEnd;
      }),
    [schedules, monthStart, monthEnd],
  );

  const payrollEmployees = useMemo(
    () => employeesForMonthlyPayroll(employees, schedulesInMonth),
    [employees, schedulesInMonth],
  );

  const monthlyInfoList = useMemo(
    () => buildMonthlyInfoList(payrollEmployees, schedulesInMonth, monthStart, monthEnd, rates),
    [payrollEmployees, schedulesInMonth, monthStart, monthEnd, rates],
  );

  const monthlyPayrollData = useMemo(
    () =>
      payrollEmployees.map((employee) => {
        const employeeSchedules = schedulesInMonth.filter(
          (s) => s.employee_id === employee.id,
        );
        return buildMonthlyPayrollData(
          employee,
          employeeSchedules,
          selectedMonth,
          {
            ...options,
            laborDayIncluded:
              options.getLaborDayIncluded?.(employee.id) ??
              options.laborDayIncluded ??
              true,
          },
        );
      }),
    [payrollEmployees, schedulesInMonth, selectedMonth, options],
  );

  const dailyWorkerNotices = useMemo(
    () =>
      buildDailyWorkerNotices(
        employees.filter((e) => !e.isRetired),
        monthlyInfoList,
        rates ?? ratesDefault2026,
      ),
    [employees, monthlyInfoList, rates],
  );

  return {
    monthlyInfoList,
    monthlyPayrollData,
    dailyWorkerNotices,
    schedulesInMonth,
  };
}

export type { EmployeeMonthlyInfo };
